import { Box, Grid, Hidden, Stack, Step, StepButton, StepIcon, StepIconProps, StepLabel, Stepper, Theme, Typography } from "@mui/material";
import { useHistory } from "react-router";
import { ApplicationProps } from "../../redux-store/types/api-types";
import { APPLICATION_LISTING_STATUS, APPLICATION_TYPE } from "../../utils/constant";
import { ApplicationDetailsCommonLayout, MenuItemList, MenuItemListLink, NonIndividualMenuItemList, NonIndividualMenuItemListLink } from "../ApplicationDetails";
import { StepperSuccessIcon } from '../../assets/SvgIcons';
import { useEffect, useRef } from "react";
import { BootstrapStepConnector } from "../StepperLoading";

interface RouterParamsData {
    applicationData: ApplicationProps
}

export default function ApplicationPreview(): JSX.Element {
    const history = useHistory();

    const routerParamsData = history.location.state as unknown as RouterParamsData

    const applicationDataRef = useRef<ApplicationProps | null>(
        routerParamsData?.applicationData || null
    );

    useEffect(() => {
        if (routerParamsData?.applicationData) {
            applicationDataRef.current = routerParamsData.applicationData;
        }
    }, [routerParamsData]);

    const applicationData = applicationDataRef.current;

    const menuList =
        applicationData &&
        (applicationData.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
            ? NonIndividualMenuItemList
            : MenuItemList);

    const applicationDetailRef = useRef<HTMLDivElement>();

    return (
        <Box sx={{ padding: '30px 45px' }}>
            <Box
                sx={{
                    marginBottom: '40px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 8px'
                }}
            >
                <Box>
                    <Typography
                        variant='h1'
                        sx={{ color: 'text.primary' }}
                    >
                        Application Details
                    </Typography>
                    <Typography
                        variant='h2'
                        sx={{ color: 'text.primary', marginTop: "20px" }}
                    >
                        {applicationData?.onboardingType?.toLowerCase() === "minor" ?
                            `Minor Investor Onboarding ${applicationData?.accountType ? '(' + applicationData?.accountType + ')' : ''}` :
                            ""
                        }
                        {(applicationData?.onboardingType?.toLowerCase() === "normal" &&
                            applicationData?.applicationType === APPLICATION_TYPE.INDIVIDUAL) ?
                            `Individual Investor Onboarding ${applicationData?.accountType ?
                                '(' + applicationData?.accountType + ')' : ''}` :
                            ""
                        }
                        {(applicationData?.onboardingType?.toLowerCase() === "normal" &&
                            applicationData?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL) ?
                            `Non Individual Investor Onboarding ${applicationData?.accountType ?
                                '(' + applicationData?.accountType + ')' : ''}` :
                            ""
                        }
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        fontWeight: '500',
                        fontSize: 16,
                        letterSpacing: '0.02px',
                        color: 'text.primary'
                    }}
                >
                    {`Application ID: ${applicationData?.applicationNumber}`}
                </Typography>
            </Box>

            <Grid container sx={(theme: Theme) => ({
                p: "35px",
                border: `1px solid ${theme.palette.text.secondary}26`,
                borderRadius: "15px",
                background: theme.palette.background.paper,
                position: 'relative'
            })}>
                <Hidden lgDown={true}>
                    <Grid
                        item
                        xs={12}
                        lg={3}
                        sx={{
                            padding: 0,
                            position: 'sticky',
                            top: '18px'
                        }}
                    >
                        <Stack
                            sx={{
                                position: 'sticky',
                                top: '18px',
                                pl: 4,
                                py: 3,
                                display: {
                                    lg: 'block',
                                    xl: 'block'
                                }
                            }}
                        >
                            {/* Stepper */}
                            <Stepper
                                activeStep={applicationData?.currentStep ?? 0}
                                orientation="vertical"
                                connector={<BootstrapStepConnector />}
                                sx={{
                                    "&.MuiStepper-vertical": {
                                        ".MuiStepButton-vertical": {
                                            ".MuiStepLabel-vertical": {
                                                paddingTop: "0px",
                                                paddingBottom: "0px"
                                            }
                                        }
                                    }
                                }}
                            >
                                {menuList &&
                                    menuList.map((item, index) => {
                                        const isItemDisabled = !(applicationData?.currentStep) ? index !== 0 : index > (applicationData?.currentStep);

                                        return (
                                            <Step key={index}>
                                                <StepButton
                                                    href={
                                                        !isItemDisabled && applicationData && applicationData.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ? `#${NonIndividualMenuItemListLink[index]}` : `#${MenuItemListLink[index]}`
                                                    }
                                                >
                                                    <StepLabel
                                                        sx={(theme: Theme) => ({
                                                            "& .MuiStepLabel-label.Mui-completed": {
                                                                ...theme.typography.body_500,
                                                                color: theme.palette.text.secondary,
                                                            },
                                                            "& .MuiStepLabel-label.Mui-active": {
                                                                ...theme.typography.body_500,
                                                                color: theme.palette.primary[500]
                                                            },
                                                            "& .MuiStepLabel-label.Mui-disabled": {
                                                                ...theme.typography.body_500,
                                                                color: theme.palette.text.secondary,
                                                            },
                                                            "& .MuiStepLabel-iconContainer.Mui-active": {
                                                                "& .MuiStepIcon-root.Mui-active": {
                                                                    color: theme.palette.primary[500]
                                                                },
                                                                "& .MuiStepIcon-text": {
                                                                    ...theme.typography.subtitle_500,
                                                                    fill: theme.palette.textDark.primary
                                                                }
                                                            },
                                                            "& .MuiStepLabel-iconContainer.Mui-disabled": {
                                                                "& .MuiStepIcon-root": {
                                                                    color: theme.palette.grey[100]
                                                                },
                                                                "& .MuiStepIcon-text": {
                                                                    ...theme.typography.subtitle_500,
                                                                    fill: theme.palette.text.secondary
                                                                }
                                                            }
                                                        })}
                                                        StepIconComponent={({ completed, ...props }: StepIconProps) => (
                                                            <StepIcon {...props} icon={completed ? <StepperSuccessIcon /> : props.icon} />
                                                        )}
                                                    >
                                                        {item}
                                                    </StepLabel>
                                                </StepButton>
                                            </Step>
                                        )
                                    })}
                            </Stepper>

                        </Stack>
                    </Grid>
                </Hidden>
                <Grid item
                    container
                    md={12}
                    lg={9}
                >
                    <Box
                        sx={(theme: Theme) => ({
                            padding: '35px',
                            border: `1px solid ${theme.palette.text.secondary}26`,
                            borderRadius: '15px',
                            backgroundColor: `${theme.palette.common.white}`,
                            width: '100%'
                        })}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 5
                            }}
                            ref={applicationDetailRef}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant='h2'
                                            sx={{
                                                color: 'text.primary',
                                                letterSpacing: '0.01px',
                                                mb: 1
                                            }}
                                        >
                                            <Typography
                                                variant='h2'
                                                sx={{
                                                    color: 'text.primary',
                                                    letterSpacing: '0.01px',
                                                    display: 'inline'
                                                }}
                                            >
                                                Application ID:{' '}
                                            </Typography>
                                            <Typography
                                                variant="inherit"
                                                sx={{
                                                    fontWeight: '400',
                                                    fontSize: 20,
                                                    display: 'inline',
                                                    color: 'text.primary',

                                                }}
                                            >
                                                {applicationData?.applicationNumber}
                                            </Typography>
                                        </Typography>

                                        <Typography
                                            variant='h2'
                                            sx={{
                                                color: 'text.primary',
                                                letterSpacing: '0.01px', mb: 1
                                            }}
                                        >
                                            <Typography
                                                variant='h2'
                                                sx={{
                                                    color: 'text.primary',
                                                    letterSpacing: '0.01px',
                                                    display: 'inline'
                                                }}
                                            >
                                                Status:{' '}
                                            </Typography>
                                            <Typography
                                                variant="inherit"
                                                sx={(theme: Theme) => ({
                                                    fontWeight: '400',
                                                    fontSize: 20,
                                                    display: 'inline',
                                                    color: applicationData?.status === 'draft' ? theme.palette.primary[500] : theme.palette.warning[500],
                                                    textTransform: 'capitalize'

                                                })}
                                            >
                                                {`${APPLICATION_LISTING_STATUS[applicationData?.status ?? '']} ${applicationData?.applicationSentBack ? '(Sent Back)' : ''}`}
                                            </Typography>
                                        </Typography>

                                    </Box>
                                    {applicationData?.clientCode &&
                                        <Box>
                                            <Typography
                                                variant='h2'
                                                sx={{
                                                    color: 'text.primary',
                                                    letterSpacing: '0.01px',
                                                    mb: 1
                                                }}
                                            >
                                                <Typography
                                                    variant='h2'
                                                    sx={{
                                                        color: 'text.primary',
                                                        letterSpacing: '0.01px',
                                                        display: 'inline'
                                                    }}
                                                >
                                                    Client Code:{' '}
                                                </Typography>
                                                <Typography
                                                    variant="inherit"
                                                    sx={{
                                                        fontWeight: '400',
                                                        fontSize: 20,
                                                        display: 'inline',
                                                        color: 'text.primary',
                                                    }}
                                                >
                                                    {applicationData?.clientCode}
                                                </Typography>
                                            </Typography>
                                        </Box>
                                    }
                                </Box>
                            </Box>
                        </Box>
                        <ApplicationDetailsCommonLayout
                            loading={false}
                            application={applicationData as unknown as ApplicationProps}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}